import React, { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
    Container,
    ContainerPadding,
    JoinField,
    PadVert10,
    InputWrapper,
    JoinInput,
    Fz15Bold,
    TabAccent,
    Gap10,
    FlxMiddleMb30,
    BtnFullLineGreen,
    Fz18Bold,
    Mb30,
    Paragraph,
    LinkLogin,
    ColAccent2,
    Pad20,
    PopShortMsg,
    BtnFullModal,
    ToggleShow,
    Mb20,
    WarnInput,
    ConfirmInfoItem,
    InfoLabel,
    InfoValue,
    BtnClearInput,
    BtnInInputRound,
} from '@/css/style';
import { emailRegex, onlyNum, phoneRegex } from '@/utils/helper';
import useForgotStore from '@/stores/common/forgot';
import NewModal from '@/components/NewModal';
import icWarn from '@/assets/img/ic_warn.svg';
import icClear from '@/assets/img/ic_clear.svg';

import authService, {
    useFindIdVerifyConfirmMutation,
    useFindPasswordVerifyConfirmMutation,
} from '@/services/authService';
import { useBlocker, useLocation, useOutletContext } from 'react-router-dom';

function Forgot() {
    const fs = useForgotStore();
    const location = useLocation();
    const { navigate } = useOutletContext();
    const findIdSmsSend = useMutation({
        mutationFn: (phoneNumber) => authService.findIdVerify(phoneNumber),
    });

    const findIdConfirm = useFindIdVerifyConfirmMutation(
        fs.fields.phone,
        (res) => {
            fs.setResponse({ data: res.data });
            fs.setState('findId', true);
            fs.setIsCert(true);
        },
        (error) => {
            const msg = error.response?.data?.message ?? error.message;
            const text = typeof msg === 'string' ? msg : '';
            if (text.includes('가입된 사용자')) {
                fs.togglePopup('verifyConfirmErrorID');
            } else {
                toast.error(text || '인증에 실패했습니다.');
            }
        }
    );

    const findPwSmsSend = useMutation({
        mutationFn: (phoneNumber) => authService.findPasswordVerify(phoneNumber),
    });

    const findPwConfirm = useFindPasswordVerifyConfirmMutation(
        fs.fields.phone,
        () => {
            const { user_id: uidRaw, phone: phoneRaw } = useForgotStore.getState().fields;
            const userId = (uidRaw || '').trim().toLowerCase();
            fs.setResponse({
                data: {
                    user_id: userId,
                    mobileno: phoneRaw,
                    name: '',
                },
            });
            fs.setState('findPw', true);
            fs.setIsCert(true);
        },
        (error) => {
            const msg = error.response?.data?.message ?? error.message;
            const text = typeof msg === 'string' ? msg : '';
            if (text.includes('가입된 사용자') || text.includes('아이디를 확인')) {
                fs.togglePopup('verifyConfirmErrorPW');
            } else {
                toast.error(text || '처리에 실패했습니다.');
            }
        }
    );

    const sendFindIdSms = () => {
        if (!phoneRegex.test(fs.fields.phone || '')) {
            toast.error('올바른 휴대폰 번호를 입력해 주세요.');
            return;
        }
        findIdSmsSend.mutate(fs.fields.phone, {
            onSuccess: () => toast.success('인증번호를 발송했습니다.'),
            onError: (error) => {
                const msg =
                    error.response?.data?.message ??
                    (typeof error === 'string' ? error : '인증번호 발송에 실패했습니다.');
                toast.error(typeof msg === 'string' ? msg : '인증번호 발송에 실패했습니다.');
            },
        });
    };

    const submitFindIdSms = () => {
        const code = (fs.fields.verifyCode || '').trim();
        if (!phoneRegex.test(fs.fields.phone || '')) {
            toast.error('올바른 휴대폰 번호를 입력해 주세요.');
            return;
        }
        if (!/^\d{4,8}$/.test(code)) {
            toast.error('인증번호를 입력해 주세요.');
            return;
        }
        findIdConfirm.mutate({ phoneNumber: fs.fields.phone, code });
    };

    const sendFindPwSms = () => {
        const userId = (fs.fields.user_id || '').trim().toLowerCase();
        if (!emailRegex.test(userId)) {
            toast.error('아이디(이메일)를 올바르게 입력해 주세요.');
            return;
        }
        if (!phoneRegex.test(fs.fields.phone || '')) {
            toast.error('올바른 휴대폰 번호를 입력해 주세요.');
            return;
        }
        findPwSmsSend.mutate(fs.fields.phone, {
            onSuccess: () => toast.success('인증번호를 발송했습니다.'),
            onError: (error) => {
                const msg =
                    error.response?.data?.message ??
                    (typeof error === 'string' ? error : '인증번호 발송에 실패했습니다.');
                toast.error(typeof msg === 'string' ? msg : '인증번호 발송에 실패했습니다.');
            },
        });
    };

    const submitFindPwSms = () => {
        const userId = (fs.fields.user_id || '').trim().toLowerCase();
        const code = (fs.fields.verifyCode || '').trim();
        if (!emailRegex.test(userId)) {
            toast.error('아이디(이메일)를 올바르게 입력해 주세요.');
            return;
        }
        if (!phoneRegex.test(fs.fields.phone || '')) {
            toast.error('올바른 휴대폰 번호를 입력해 주세요.');
            return;
        }
        if (!/^\d{4,8}$/.test(code)) {
            toast.error('인증번호를 입력해 주세요.');
            return;
        }
        findPwConfirm.mutate({
            phoneNumber: fs.fields.phone,
            code,
            user_id: userId,
        });
    };

    useEffect(() => {
        return () => {
            fs.terminate();
        };
    }, []);
    /** NICE 콜백 등에서만 막고, 인증 완료 후 `/login`(로그인하기) 이동은 허용 */
    let blocker = useBlocker(({ nextLocation }) => {
        return nextLocation.pathname === '/nice/callback' && fs.isCert;
    });

    useEffect(() => {
        if (blocker.state === 'blocked') {
            window.ReactNativeWebView && navigate(-5, { replace: true });
        }
    }, [blocker.state]);
    return (
        <>
            <Container>
                <ContainerPadding>
                    <FlxMiddleMb30>
                        <TabAccent
                            $active={fs.screen.id}
                            onClick={() => {
                                fs.setScreen('id');
                                fs.screenTerminate();
                                fs.setField('receiveData', 'find-id');
                            }}
                        >
                            아이디 찾기
                        </TabAccent>
                        <Gap10 />
                        <TabAccent
                            $active={fs.screen.password}
                            onClick={() => {
                                fs.setScreen('password');
                                fs.screenTerminate();
                                fs.setField('receiveData', 'find-pw');
                            }}
                        >
                            비밀번호 찾기
                        </TabAccent>
                    </FlxMiddleMb30>
                    <ToggleShow $visible={fs.screen.id}>
                        {Object.keys(fs.res).length === 0 && (
                            <>
                                <Fz18Bold $mb="3.846rem">
                                    가입하셨던 휴대폰 번호로 인증해 주세요
                                </Fz18Bold>
                                <JoinField>
                                    <PadVert10>
                                        <Fz15Bold>휴대폰번호</Fz15Bold>
                                    </PadVert10>
                                    <InputWrapper>
                                        <JoinInput
                                            type="tel"
                                            inputMode="numeric"
                                            id="findIdPhone"
                                            name="findIdPhone"
                                            placeholder="'-' 없이 숫자만 입력해 주세요"
                                            maxLength={11}
                                            autoComplete="tel"
                                            value={fs.fields.phone}
                                            onChange={(e) =>
                                                fs.setField('phone', onlyNum(e.target.value))
                                            }
                                        />
                                        <BtnInInputRound
                                            type="button"
                                            onClick={sendFindIdSms}
                                            disabled={findIdSmsSend.isPending}
                                        >
                                            인증번호 받기
                                        </BtnInInputRound>
                                        <BtnClearInput
                                            type="button"
                                            onClick={() => fs.setField('phone', '')}
                                            aria-label="입력 지우기"
                                        >
                                            <img src={icClear} alt="지우기" />
                                        </BtnClearInput>
                                    </InputWrapper>
                                    {fs.fields.phone &&
                                    fs.fields.phone.length > 0 &&
                                    !phoneRegex.test(fs.fields.phone) ? (
                                        <WarnInput>
                                            <img src={icWarn} alt="경고" /> 올바른 휴대폰 번호를
                                            입력해 주세요
                                        </WarnInput>
                                    ) : null}
                                </JoinField>
                                <JoinField>
                                    <PadVert10>
                                        <Fz15Bold>인증번호</Fz15Bold>
                                    </PadVert10>
                                    <InputWrapper>
                                        <JoinInput
                                            type="text"
                                            inputMode="numeric"
                                            id="findIdCode"
                                            name="findIdCode"
                                            placeholder="인증번호를 입력해 주세요"
                                            maxLength={8}
                                            value={fs.fields.verifyCode}
                                            onChange={(e) =>
                                                fs.setField('verifyCode', onlyNum(e.target.value))
                                            }
                                        />
                                        <BtnClearInput
                                            type="button"
                                            onClick={() => fs.setField('verifyCode', '')}
                                            aria-label="입력 지우기"
                                        >
                                            <img src={icClear} alt="지우기" />
                                        </BtnClearInput>
                                    </InputWrapper>
                                </JoinField>
                                <BtnFullLineGreen
                                    onClick={submitFindIdSms}
                                    disabled={findIdConfirm.isPending}
                                >
                                    아이디 확인
                                </BtnFullLineGreen>
                            </>
                        )}

                        {fs.findId && (
                            <>
                                <Fz18Bold $mb="3.846rem">정보 확인</Fz18Bold>
                                <Mb30>
                                    {fs.res.data?.name ? (
                                        <ConfirmInfoItem>
                                            <InfoLabel>이름</InfoLabel>
                                            <InfoValue>{fs.res.data.name}</InfoValue>
                                        </ConfirmInfoItem>
                                    ) : null}
                                    <ConfirmInfoItem>
                                        <InfoLabel>휴대폰번호</InfoLabel>
                                        <InfoValue>{fs.res.data.mobileno}</InfoValue>
                                    </ConfirmInfoItem>
                                </Mb30>
                                <Paragraph $fz="1.154rem" $ta="center" as={Mb30}>
                                    회원님의 아이디는
                                    <br />
                                    <ColAccent2>{fs.res.data.user_id}</ColAccent2> 입니다.
                                </Paragraph>
                                <LinkLogin to="/login" state={{ previousUrl: location.pathname }}>
                                    로그인하기
                                </LinkLogin>
                            </>
                        )}
                        {/* //아이디 안내 */}
                    </ToggleShow>
                    <ToggleShow $visible={fs.screen.password}>
                        {Object.keys(fs.res).length === 0 && (
                            <>
                                <Fz18Bold $mb="3.846rem">
                                    가입하셨던 아이디와 휴대폰 번호로 인증해 주세요
                                </Fz18Bold>
                                <JoinField>
                                    <PadVert10>
                                        <Fz15Bold>아이디</Fz15Bold>
                                    </PadVert10>
                                    <InputWrapper>
                                        <JoinInput
                                            type="text"
                                            id="userId"
                                            name="userId"
                                            placeholder="아이디(이메일)를 입력해 주세요"
                                            autoComplete="username"
                                            onChange={(e) => fs.setField('user_id', e.target.value)}
                                            value={fs.fields.user_id ? fs.fields.user_id : ''}
                                        />
                                        <BtnClearInput
                                            type="button"
                                            onClick={() => fs.setField('user_id', '')}
                                            aria-label="입력 지우기"
                                        >
                                            <img src={icClear} alt="지우기" />
                                        </BtnClearInput>
                                    </InputWrapper>
                                    {fs.fields.user_id &&
                                    fs.fields.user_id.length > 0 &&
                                    !emailRegex.test(fs.fields.user_id) ? (
                                        <WarnInput>
                                            <img src={icWarn} alt="경고" /> 이메일 형식이 맞지
                                            않습니다.
                                        </WarnInput>
                                    ) : null}
                                </JoinField>
                                <JoinField>
                                    <PadVert10>
                                        <Fz15Bold>휴대폰번호</Fz15Bold>
                                    </PadVert10>
                                    <InputWrapper>
                                        <JoinInput
                                            type="tel"
                                            inputMode="numeric"
                                            id="findPwPhone"
                                            name="findPwPhone"
                                            placeholder="'-' 없이 숫자만 입력해 주세요"
                                            maxLength={11}
                                            autoComplete="tel"
                                            value={fs.fields.phone}
                                            onChange={(e) =>
                                                fs.setField('phone', onlyNum(e.target.value))
                                            }
                                        />
                                        <BtnInInputRound
                                            type="button"
                                            onClick={sendFindPwSms}
                                            disabled={findPwSmsSend.isPending}
                                        >
                                            인증번호 받기
                                        </BtnInInputRound>
                                        <BtnClearInput
                                            type="button"
                                            onClick={() => fs.setField('phone', '')}
                                            aria-label="입력 지우기"
                                        >
                                            <img src={icClear} alt="지우기" />
                                        </BtnClearInput>
                                    </InputWrapper>
                                    {fs.fields.phone &&
                                    fs.fields.phone.length > 0 &&
                                    !phoneRegex.test(fs.fields.phone) ? (
                                        <WarnInput>
                                            <img src={icWarn} alt="경고" /> 올바른 휴대폰 번호를
                                            입력해 주세요
                                        </WarnInput>
                                    ) : null}
                                </JoinField>
                                <JoinField>
                                    <PadVert10>
                                        <Fz15Bold>인증번호</Fz15Bold>
                                    </PadVert10>
                                    <InputWrapper>
                                        <JoinInput
                                            type="text"
                                            inputMode="numeric"
                                            id="findPwCode"
                                            name="findPwCode"
                                            placeholder="인증번호를 입력해 주세요"
                                            maxLength={8}
                                            value={fs.fields.verifyCode}
                                            onChange={(e) =>
                                                fs.setField('verifyCode', onlyNum(e.target.value))
                                            }
                                        />
                                        <BtnClearInput
                                            type="button"
                                            onClick={() => fs.setField('verifyCode', '')}
                                            aria-label="입력 지우기"
                                        >
                                            <img src={icClear} alt="지우기" />
                                        </BtnClearInput>
                                    </InputWrapper>
                                </JoinField>
                                <BtnFullLineGreen
                                    onClick={submitFindPwSms}
                                    disabled={findPwConfirm.isPending}
                                >
                                    임시 비밀번호 받기
                                </BtnFullLineGreen>
                            </>
                        )}

                        {fs.findPw && (
                            <>
                                <Fz18Bold $mb="3.846rem">정보 확인</Fz18Bold>
                                <Mb30>
                                    {fs.res.data?.name ? (
                                        <ConfirmInfoItem>
                                            <InfoLabel>이름</InfoLabel>
                                            <InfoValue>{fs.res.data.name}</InfoValue>
                                        </ConfirmInfoItem>
                                    ) : null}
                                    <ConfirmInfoItem>
                                        <InfoLabel>휴대폰번호</InfoLabel>
                                        <InfoValue>{fs.res.data.mobileno}</InfoValue>
                                    </ConfirmInfoItem>
                                    <ConfirmInfoItem>
                                        <InfoLabel>아이디</InfoLabel>
                                        <InfoValue>{fs.res.data.user_id}</InfoValue>
                                    </ConfirmInfoItem>
                                </Mb30>
                                <Paragraph $fz="1.154rem" $ta="center" as={Mb30}>
                                    문자로 <ColAccent2>임시 비밀번호</ColAccent2>를 보냈습니다.
                                    <br />
                                    비밀번호는 마이페이지에서 변경 가능합니다.
                                </Paragraph>
                                <LinkLogin to="/login" state={{ previousUrl: location.pathname }}>
                                    로그인하기
                                </LinkLogin>
                            </>
                        )}
                        {/* //아이디 안내 */}
                    </ToggleShow>
                </ContainerPadding>
            </Container>
            <NewModal
                isOpen={fs.popup.verifyConfirmErrorID}
                contentLabel="사용자 정보 오류"
                ariaHideApp={false}
            >
                <Pad20>
                    <PopShortMsg>
                        <Mb20>
                            <Fz15Bold $col="#000">가입된 사용자가 없습니다.</Fz15Bold>
                        </Mb20>
                    </PopShortMsg>
                    <BtnFullModal $active onClick={() => fs.togglePopup('verifyConfirmErrorID')}>
                        확인
                    </BtnFullModal>
                </Pad20>
            </NewModal>
            <NewModal
                isOpen={fs.popup.verifyConfirmErrorPW}
                contentLabel="사용자 정보 오류"
                ariaHideApp={false}
            >
                <Pad20>
                    <PopShortMsg>
                        <Mb20>
                            <Fz15Bold $col="#000">비밀번호를 찾을 수 없습니다.</Fz15Bold>
                        </Mb20>
                    </PopShortMsg>
                    <BtnFullModal $active onClick={() => fs.togglePopup('verifyConfirmErrorPW')}>
                        확인
                    </BtnFullModal>
                </Pad20>
            </NewModal>
        </>
    );
}

export default Forgot;
